// What the scroll says, laid out each frame from the camera: the realms' names, the lands down the left,
// the ages and years along the top, and the needle that shows the whole world at one moment.
//
// The rule for every word: horizontal, at least MIN_TEXT px, never squashed or clipped. A name that does not
// fit is not drawn; the needle names it the moment you point at it, and zooming in makes room for it.

import { fmtYear } from "@hoee/chart";
import type { Block, Chart, Realm } from "@hoee/chart";
import { Fades } from "./fade.ts";
import type { Labels, TextStyle } from "./gl/labels.ts";
import type { Rules } from "./gl/rules.ts";
import { FONTS } from "./theme.ts";
import type { Camera } from "./view/camera.ts";

export const MIN_TEXT = 12;

interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
/** A rectangle in the world: lane units across (x0, x1), years along (y0, y1); partial when it covers hatching. */
type Place = Rect & { partial?: boolean };

const NAME: TextStyle = { font: FONTS.text, weight: 600 };
const DATES: TextStyle = { font: FONTS.text, italic: true };
const TAGLINE: TextStyle = { font: FONTS.text, italic: true };
const YEAR: TextStyle = { font: FONTS.text };
const NEEDLE_YEAR: TextStyle = { font: FONTS.text, weight: 600 };
const TAG: TextStyle = { font: FONTS.text, weight: 600 };
const PARTIAL: TextStyle = { font: FONTS.text, italic: true };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Matches whenOf in @hoee/chart: a date's place within its year.
const MONTH_START = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

export const spanText = (r: Realm, now: number): string =>
  `${r.fades ? "before " : ""}${fmtYear(r.start)} – ${r.end >= now ? "today" : fmtYear(r.end)}`;

export function smooth(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** Splits text into two lines of about equal width, at a space. */
function halves(text: string): [string, string] | null {
  const words = text.split(" ");
  if (words.length < 2) return null;
  let best: [string, string] | null = null,
    diff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(" "),
      b = words.slice(i).join(" ");
    const d = Math.abs(a.length - b.length);
    if (d < diff) {
      diff = d;
      best = [a, b];
    }
  }
  return best;
}

export class WorldText {
  /** The realm whose page is open, and how far it has opened. */
  focus: Realm | null = null;
  focusMix = 0;
  /** The needle: the pointer's x, and how visible it is (0..1). */
  needleX = 0;
  needle = 0;
  private readonly chart: Chart;
  private readonly labels: Labels;
  private readonly rules: Rules;
  /** Drawn over everything: the needle and its tags. */
  private readonly overlay: Rules;
  private readonly now = new Date().getFullYear();
  /** Blocks of each lane, for finding who held it in a given year. */
  private readonly byLane: Block[][];
  /** Every rectangle a realm's name could go in (lane units across, years along); the most legible wins. */
  private readonly places = new Map<Realm, Place[]>();
  /** The needle's tags this frame: names under them step aside. */
  private tags: Rect[] = [];
  /** Realms whose names are readable on screen this frame: the needle need not name them again. */
  private named = new Set<Realm>();
  /** Names ease in as they become readable, instead of showing faint while they are still too small. */
  private readonly fades = new Fades();
  private k = 1;
  /** Names placed this frame, drawn once the needle's tags are known. */
  private pending: { rect: Rect; alpha: number; draw: (alpha: number) => void }[] = [];

  constructor(chart: Chart, labels: Labels, rules: Rules, overlay: Rules) {
    this.chart = chart;
    this.labels = labels;
    this.rules = rules;
    this.overlay = overlay;
    this.byLane = chart.columns.map((_, i) => chart.blocks.filter((b) => Math.floor(b.x0) === i));
    for (const r of chart.realms) {
      // Runs of one realm's blocks in one lane, joined end to end, plus its biggest box across lanes.
      const runs: Place[] = [];
      for (const b of [...r.blocks].sort((p, q) => p.x0 - q.x0 || p.cy0 - q.cy0)) {
        const last = runs.at(-1);
        if (last && last.x0 === b.x0 && last.x1 === b.x1 && last.y1 >= b.cy0 && !!last.partial === !!b.partial)
          last.y1 = Math.max(last.y1, b.y1);
        else runs.push({ x0: b.x0, x1: b.x1, y0: b.cy0, y1: b.y1, partial: !!b.partial });
      }
      // Neighbouring lanes (or halves of one) held over the same years read as one wider band.
      const singles = [...runs];
      for (const a of singles)
        for (const b of singles) {
          if (a.x1 !== b.x0) continue;
          const y0 = Math.max(a.y0, b.y0),
            y1 = Math.min(a.y1, b.y1);
          if (y1 > y0) runs.push({ x0: a.x0, x1: b.x1, y0, y1, partial: !!(a.partial || b.partial) });
        }
      for (const box of [r.nameBox.true]) {
        if (!box) continue;
        const partial = r.blocks.some(
          (b) => b.partial && b.x0 < box.x1 && box.x0 < b.x1 && b.cy0 < box.y1 && box.y0 < b.y1,
        );
        runs.push({ ...box, partial });
      }
      this.places.set(r, runs);
    }
  }

  /** Lays out this frame. Returns whether any name is still easing in. */
  layout(camera: Camera, dt: number): boolean {
    this.k = 1 - Math.exp(-dt / 0.07);
    this.fades.begin();
    this.tags = [];
    this.named.clear();
    this.pending = [];
    this.realmNames(camera);
    if (this.needle > 0.01) this.drawNeedle(camera);
    for (const p of this.pending) {
      const r = p.rect;
      const covered = this.tags.some((t) => t.x0 < r.x1 && r.x0 < t.x1 && t.y0 < r.y1 && r.y0 < t.y1);
      p.draw(covered ? p.alpha * (1 - this.needle) : p.alpha);
    }
    this.partialNotes(camera);
    this.time(camera);
    return this.fades.busy;
  }

  /**
   * How tall each lane wants to be: a land split between two realms in the years on screen grows, so each half
   * has room for its name. Weights change smoothly with how much of the split is visible, so nothing flickers.
   */
  laneWeights(camera: Camera): number[] {
    const { x0: ix0, x1: ix1 } = camera.inner;
    const from = camera.yearAt(ix0),
      to = camera.yearAt(ix1);
    return this.byLane.map((blocks) => {
      let widest = 0;
      for (const b of blocks) {
        if (!b.part || b.y1 <= from || b.cy0 >= to) continue;
        widest = Math.max(widest, Math.min(camera.xOf(b.y1), ix1) - Math.max(camera.xOf(b.cy0), ix0));
      }
      return 1 + 0.8 * smooth(40, 160, widest);
    });
  }

  /** How big a realm's name can be set in a rectangle on screen, and on how many lines. */
  private fit(r: Realm, w: number, h: number): { size: number; lines: string[] } {
    const L = this.labels;
    // A name may grow with its band: a land brought forward gives its realms room for big, clear names.
    const cap = h > 120 ? 30 : h > 70 ? 24 : 19;
    let lines = [r.name];
    let size = Math.min(cap, (h - 8) / 1.25, (w - 16) / L.measure(r.name, NAME));
    const two = halves(r.name);
    if (two) {
      const wide = Math.max(L.measure(two[0], NAME), L.measure(two[1], NAME));
      const s2 = Math.min(cap, (h - 8) / 2.4, (w - 16) / wide);
      if (s2 > size * 1.15) {
        size = s2;
        lines = two;
      }
    }
    return { size, lines };
  }

  private realmNames(camera: Camera): void {
    const L = this.labels;
    const { plot } = camera;
    const { x0: ix0, x1: ix1 } = camera.inner;
    const top = plot.top,
      bottom = plot.top + plot.height;
    for (const r of this.chart.realms) {
      // Of every place the name could go, the one where it reads biggest right now.
      let best: {
        x0: number;
        x1: number;
        y0: number;
        y1: number;
        size: number;
        lines: string[];
        box: Place;
        score: number;
      } | null = null;
      for (const box of this.places.get(r) ?? []) {
        const x0 = Math.max(camera.xOf(box.y0), ix0),
          x1 = Math.min(camera.xOf(box.y1), ix1);
        const y0 = Math.max(camera.yOf(box.x0), top),
          y1 = Math.min(camera.yOf(box.x1), bottom);
        if (x1 - x0 < 40 || y1 - y0 < MIN_TEXT + 4) continue;
        const f = this.fit(r, x1 - x0, y1 - y0);
        // Hatching is hard to read over: a name goes there only when it has nowhere much better.
        const score = f.size * (box.partial ? 0.72 : 1);
        if (!best || score > best.score + 0.01) best = { x0, x1, y0, y1, ...f, box, score };
      }
      if (!best || best.size < MIN_TEXT) {
        this.fades.get(`name:${r.id}`, false, 1);
        continue;
      }
      const { x0, x1, y0, y1, lines, size } = best;
      const w = x1 - x0,
        h = y1 - y0;
      const focused = this.focus === r;
      const alpha = focused ? 1 - this.focusMix : this.focus ? 1 - 0.5 * this.focusMix : 1;
      if (alpha < 0.02) continue;
      // Below the name, when there is room: its years, then its tagline.
      const dates = spanText(r, this.now);
      const extra: { text: string; style: TextStyle; size: number }[] = [];
      let used = lines.length * size * 1.15;
      const small = Math.max(MIN_TEXT, Math.min(17, size * 0.62));
      if (h - 10 - used > small * 1.4 && L.measure(dates, DATES) * small < w - 16) {
        extra.push({ text: dates, style: DATES, size: small });
        used += small * 1.35;
        if (h - 10 - used > small * 1.4 && L.measure(r.tagline, TAGLINE) * small < w - 16) {
          extra.push({ text: r.tagline, style: TAGLINE, size: small });
          used += small * 1.35;
        }
      }
      const cx = (x0 + x1) / 2;
      const nameW = Math.max(...lines.map((l) => L.measure(l, NAME))) * size;
      const half = used / 2 + 4;
      this.named.add(r);
      this.pending.push({
        rect: { x0: cx - nameW / 2, x1: cx + nameW / 2, y0: (y0 + y1) / 2 - half, y1: (y0 + y1) / 2 + half },
        // Readable or not drawn: a name never shows faint. It eases in when it first fits.
        alpha: alpha * this.fades.get(`name:${r.id}`, true, this.k),
        draw: (a) => {
          if (a < 0.02) return;
          let y = (y0 + y1) / 2 - used / 2 + size * 0.58;
          for (const line of lines) {
            L.draw({ text: line, style: NAME, x: cx, y, size, alpha: a });
            y += size * 1.15;
          }
          y += 2;
          for (const e of extra) {
            L.draw({ text: e.text, style: e.style, x: cx, y: y - 2, size: e.size, alpha: a, tone: 1 });
            y += e.size * 1.35;
          }
        },
      });
    }
  }

  /**
   * Hatching means a realm held a land only in part (the poster draws such blocks faded: Roman rule in Germania,
   * say). Wherever there is room, say so in words, on a small paper tag over the stripes.
   */
  private partialNotes(camera: Camera): void {
    const L = this.labels;
    const { plot } = camera;
    const { x0: ix0, x1: ix1 } = camera.inner;
    const size = 12.5;
    for (const b of this.chart.blocks) {
      if (!b.partial) continue;
      const x0 = Math.max(camera.xOf(b.cy0), ix0),
        x1 = Math.min(camera.xOf(b.y1), ix1);
      const y0 = Math.max(camera.yOf(b.x0), plot.top),
        y1 = Math.min(camera.yOf(b.x1), plot.top + plot.height);
      if (y1 - y0 < 18 || x1 - x0 < 60) continue;
      const land = this.chart.columns[Math.floor(b.x0)]?.name ?? "";
      const options = [`${b.realm.name} ruled ${land} only in part`, `${b.realm.name}, in part`, "ruled in part"];
      const text = options.find((t) => L.measure(t, PARTIAL) * size + 22 < x1 - x0);
      if (!text) continue;
      const w = L.measure(text, PARTIAL) * size + 18;
      const cx = (x0 + x1) / 2,
        cy = (y0 + y1) / 2;
      // Names come first: the tag steps aside rather than sit on one.
      const box = { x0: cx - w / 2, x1: cx + w / 2, y0: cy - 11, y1: cy + 11 };
      if (this.pending.some(({ rect: r }) => r.x0 < box.x1 && box.x0 < r.x1 && r.y0 < box.y1 && box.y0 < r.y1))
        continue;
      this.rules.draw({
        x0: cx - w / 2,
        y0: cy - 10,
        x1: cx + w / 2,
        y1: cy + 10,
        alpha: 0.92,
        pigment: -2,
        radius: 10,
      });
      L.draw({ text, style: PARTIAL, x: cx, y: cy, size, alpha: 1, tone: 1 });
    }
  }

  private time(camera: Camera): void {
    const L = this.labels,
      R = this.rules;
    const { plot } = camera;
    const { x0: ix0, x1: ix1 } = camera.inner;
    const yearY = plot.top - 14;
    R.draw({ x0: plot.left, y0: plot.top - 0.5, x1: plot.left + plot.width, y1: plot.top + 0.5, alpha: 0.22 });

    // Years (or months, deep in) along the top. Labels need about 90 px each.
    const mid = camera.yearAt((ix0 + ix1) / 2);
    const ppy = camera.pxPerYear(mid);
    // The needle's year tag sits on this row; labels it would cover step aside.
    const nx = Math.min(ix1, Math.max(ix0, this.needleX));
    const tagHalf = (L.measure(this.needleText(camera, camera.yearAt(nx)), NEEDLE_YEAR) * 15) / 2 + 12;
    const tick = (at: number, text: string) => {
      const x = camera.xOf(at);
      if (x < ix0 - 2 || x > ix1 + 2) return;
      const half = (L.measure(text, YEAR) * 13) / 2;
      const hidden = Math.abs(x - nx) < half + tagHalf ? this.needle : 0;
      R.draw({ x0: x - 0.5, y0: plot.top - 5, x1: x + 0.5, y1: plot.top, alpha: 0.4 });
      L.draw({ text, style: YEAR, x, y: yearY, size: 13, alpha: 0.9 * (1 - hidden), tone: 1 });
    };
    const first = camera.yearAt(ix0),
      // A hair past the edge, so a tick that falls right on it (AD 2000 at the end of the chart) is kept.
      last = camera.yearAt(ix1) + 1e-6;
    if (ppy >= 90 * 2) {
      const every = [1, 2, 3, 6].find((k) => (ppy * k) / 12 >= 90) ?? 12;
      for (let y = Math.floor(first); y <= last; y++) {
        if (y === 0) continue;
        for (let m = 0; m < 12; m += every)
          tick(y + (MONTH_START[m] as number) / 365, m === 0 ? fmtYear(y) : `${MONTHS[m]} ${fmtYear(y)}`);
      }
    } else {
      const steps = [1000, 500, 250, 100, 50, 25, 10, 5, 2, 1];
      const step = [...steps].reverse().find((k) => k * ppy >= 90) ?? 1000;
      for (let y = Math.ceil(first / step) * step; y <= last; y += step)
        tick(y === 0 ? 1 : y, fmtYear(y === 0 ? 1 : y));
    }
  }

  /** The needle's date: the year, or the month and year once a year is wide enough to tell months apart. */
  private needleText(camera: Camera, at: number): string {
    if (camera.pxPerYear(at) < 240) return fmtYear(Math.round(at) || 1);
    const y = Math.floor(at);
    const day = (at - y) * 365;
    let m = 11;
    while (m > 0 && day < (MONTH_START[m] as number)) m--;
    return `${MONTHS[m]} ${fmtYear(y || 1)}`;
  }

  /** Who held each land in a year, with the lane span (in lane units) each holder covers. */
  holdersAt(year: number): { lane: number; block: Block }[] {
    const out: { lane: number; block: Block }[] = [];
    this.byLane.forEach((blocks, lane) => {
      for (const b of blocks) if (b.cy0 <= year && year < b.y1) out.push({ lane, block: b });
    });
    return out;
  }

  private drawNeedle(camera: Camera): void {
    const L = this.labels;
    const { plot } = camera;
    const { x0: ix0, x1: ix1 } = camera.inner;
    const x = Math.min(ix1, Math.max(ix0, this.needleX));
    const a = this.needle;
    const year = camera.yearAt(x);
    // The line runs under the words, so it never cuts through a name.
    this.rules.draw({ x0: x - 0.6, y0: plot.top, x1: x + 0.6, y1: plot.top + plot.height, alpha: 0.6 * a });

    // The year, on a tag at the top.
    const ys = 15;
    const text = this.needleText(camera, year);
    const tw = L.measure(text, NEEDLE_YEAR) * ys;
    this.chip(x - tw / 2 - 9, plot.top - 27, x + tw / 2 + 9, plot.top - 5, a);
    L.draw({ text, style: NEEDLE_YEAR, x, y: plot.top - 16, size: ys, alpha: a, layer: 1 });

    // Every land, in that year: one tag per realm, across all the lanes it holds together.
    const size = 13;
    const spans: { realm: Realm; x0: number; x1: number; partial: boolean }[] = [];
    for (const { block } of this.holdersAt(year).sort((p, q) => p.block.x0 - q.block.x0)) {
      const last = spans.at(-1);
      const partial = !!block.partial;
      if (last && last.realm === block.realm && last.x1 >= block.x0 && last.partial === partial)
        last.x1 = Math.max(last.x1, block.x1);
      else spans.push({ realm: block.realm, x0: block.x0, x1: block.x1, partial });
    }
    // Tags go out from the needle; two halves of one land sit side by side on its middle.
    const rowX = new Map<number, number>();
    for (const { realm: r, x0: l0, x1: l1, partial } of spans) {
      if (this.focus === r && this.focusMix > 0.5) continue;
      if (this.named.has(r) && !partial) continue;
      const name = partial ? `${r.name}, in part` : r.name;
      const y0 = camera.yOf(l0),
        y1 = camera.yOf(l1);
      const lane = Math.floor(l0);
      const sameLane = l1 - l0 < 1 && y1 - y0 < 23;
      const cy = sameLane ? (camera.yOf(lane) + camera.yOf(lane + 1)) / 2 : (y0 + y1) / 2;
      if ((sameLane ? camera.yOf(lane + 1) - camera.yOf(lane) : y1 - y0) < 19) continue;
      const w = L.measure(name, TAG) * size + 18;
      const start = sameLane ? (rowX.get(lane) ?? x + 10) : x + 10;
      const right = start + w < ix1;
      const cx0 = right ? start : x - 10 - w - (sameLane ? (rowX.get(-1 - lane) ?? 0) : 0);
      if (sameLane) {
        if (right) rowX.set(lane, start + w + 6);
        else rowX.set(-1 - lane, (rowX.get(-1 - lane) ?? 0) + w + 6);
      }
      this.chip(cx0, cy - 10, cx0 + w, cy + 10, a * 0.97);
      this.tags.push({ x0: cx0 - 6, y0: cy - 12, x1: cx0 + w + 6, y1: cy + 12 });
      L.draw({ text: name, style: TAG, x: cx0 + w / 2, y: cy, size, alpha: a, layer: 1 });
    }
  }

  /** A small paper tag with a soft shadow, for text that sits over paint. */
  chip(x0: number, y0: number, x1: number, y1: number, alpha: number): void {
    this.overlay.draw({ x0: x0 - 3, y0: y0 + 1, x1: x1 + 3, y1: y1 + 5, alpha: 0.18 * alpha, pigment: -3, radius: 8 });
    this.overlay.draw({ x0, y0, x1, y1, alpha, pigment: -2, radius: (y1 - y0) / 2 });
  }
}
